import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { endpoints } from "../../services/endpoints";

import { fmtDate } from "../../utils/format";
import { padlockStatusClass, padlockStatusLabel } from "../../utils/padlock";
import { getUserDisplayName } from "../../utils/user";

export default function DashboardCompaniesId() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [padlocks, setPadlocks] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr(null);
      try {
        // 1) Traer compañía (idealmente con relaciones)
        const cRes = await api.get(endpoints.companies.byId(id));
        const c = cRes.data?.data ?? cRes.data;
        if (!alive) return;

        setCompany(c || null);

        // 2) Si vienen relaciones, perfecto
        const pads = Array.isArray(c?.padlocks) ? c.padlocks : [];
        const usrs = Array.isArray(c?.users) ? c.users : [];

        setPadlocks(pads);
        setUsers(usrs);

        // 3) Si NO vinieran, fallbacks por params
        if (!pads.length) {
          try {
            const pList = await api.get(endpoints.padlock.list, {
              params: { companyId: id },
            });
            const list = pList.data?.data ?? pList.data ?? [];
            if (alive) setPadlocks(Array.isArray(list) ? list : []);
          } catch (_) {}
        }

        if (!usrs.length) {
          try {
            const uList = await api.get(endpoints.users.list, {
              params: { companyId: id },
            });
            const list = uList.data?.data ?? uList.data ?? [];
            if (alive) setUsers(Array.isArray(list) ? list : []);
          } catch (_) {}
        }
      } catch (e) {
        if (!alive) return;
        console.error(e);
        setErr(e);
      } finally {
        if (alive) setLoading(false);
      }
    }

    if (id) load();
    return () => { alive = false; };
  }, [id]);

  const contactName = useMemo(() => {
    return company?.contact ? getUserDisplayName(company.contact) : "—";
  }, [company]);

  if (loading) return <div className="p-6">Cargando…</div>;

  if (err) {
    return (
      <div className="p-6">
        <p className="text-red-600 font-semibold">Error cargando la compañía</p>
        <pre className="text-sm text-gray-700 whitespace-pre-wrap">{String(err)}</pre>
        <button
          className="mt-3 px-3 py-2 border rounded"
          onClick={() => navigate("/dashboardclientesadmin")}
        >
          Volver al listado
        </button>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-6">
        <p>No se encontró la compañía.</p>
        <button
          className="mt-3 px-3 py-2 border rounded"
          onClick={() => navigate("/dashboardclientesadmin")}
        >
          Volver al listado
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header + acciones */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{company.name}</h1>
          <div className="text-gray-600">NIF/CIF: {company.taxId}</div>
          <div className="text-gray-600">Contacto principal: {contactName}</div>
        </div>
        <div className="flex gap-2">
          <button
            className="px-3 py-2 rounded border text-blue-600"
            onClick={() => navigate(`/companies/${company.id}/edit`)}
          >
            Editar
          </button>
          <button
            className="px-3 py-2 rounded border"
            onClick={() => navigate("/dashboardclientesadmin")}
          >
            Volver
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded shadow">
          <div className="text-sm text-gray-500">Total candados</div>
          <div className="text-2xl font-semibold">{padlocks.length}</div>
        </div>
        <div className="bg-white p-4 rounded shadow">
          <div className="text-sm text-gray-500">Total usuarios</div>
          <div className="text-2xl font-semibold">{users.length}</div>
        </div>
        <div className="bg-white p-4 rounded shadow">
          <div className="text-sm text-gray-500">Contacto</div>
          <div className="text-lg">{contactName}</div>
        </div>
      </div>

      {/* Tabla candados */}
      <div className="bg-white p-4 rounded shadow">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Candados</h2>
          <span className="text-sm text-gray-500">Mostrando {padlocks.length}</span>
        </div>
        <div className="overflow-x-auto mt-3">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 text-left">Nombre</th>
                <th className="p-2 text-left">Serial</th>
                <th className="p-2 text-left">Estado</th>
                <th className="p-2 text-left">Actualizado</th>
                <th className="p-2 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {padlocks.length ? (
                padlocks.map((p) => (
                  <tr key={p.id} className="border-b">
                    <td className="p-2">{p.name ?? "—"}</td>
                    <td className="p-2">{p.serialNumber ?? "—"}</td>
                    <td className="p-2">
                      <span className={padlockStatusClass(p.status)}>
                        {padlockStatusLabel(p.status)}
                      </span>
                    </td>
                    <td className="p-2">{fmtDate(p.updated ?? p.updatedAt)}</td>
                    <td className="p-2">
                      <button
                        className="px-3 py-1 rounded border text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                        disabled={!p.serialNumber}
                        onClick={() => {
                          if (p.serialNumber) {
                            navigate(`/locksadmin/${encodeURIComponent(p.serialNumber)}`);
                          }
                        }}
                      >
                        Ver candado
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="p-3 text-center text-gray-500">
                    Sin candados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tabla usuarios */}
      <div className="bg-white p-4 rounded shadow">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Usuarios</h2>
          <span className="text-sm text-gray-500">Mostrando {users.length}</span>
        </div>
        <div className="overflow-x-auto mt-3">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 text-left">Nombre</th>
                <th className="p-2 text-left">Identificación</th>
                <th className="p-2 text-left">Rol</th>
              </tr>
            </thead>
            <tbody>
              {users.length ? (
                users.map((u) => (
                  <tr key={u.id} className="border-b">
                    <td className="p-2">{u.name ?? "—"}</td>
                    <td className="p-2">{u.identification ?? u.dni ?? "—"}</td>
                    <td className="p-2">{u.role ?? u.typeUser ?? "—"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="p-3 text-center text-gray-500">
                    Sin usuarios
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}